import { AxiosResponse } from 'axios';
import { useMutation, UseMutationOptions, useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';

import { AgencyResponseDto, CreateIVRDto, IVRResponseDto, UpdateIvrDto } from '@bandido/shared/dtos';

import { QueryKeys } from '../common/types';
import { client } from '../common/services';
import { useSnackBar } from '../common/components/SnackBarContext';

const getAgencyIVR = async (agencyId: number) => client.get<IVRResponseDto>(`/agency/${agencyId}/ivr-settings`);
const createAgencyIVR = async (id: number, data: CreateIVRDto) =>
  client.post<IVRResponseDto>(`/agency/${id}/ivr-settings`, data);

const updateAgencyIVR = async (id: AgencyResponseDto['id'], data: UpdateIvrDto) =>
  client.patch<IVRResponseDto>(`/agency/${id}/ivr-settings`, data);

export const useGetAgencyIVR = (agencyId?: number) =>
  useQuery({
    queryKey: [QueryKeys.getAgencyIVR, { agencyId }],
    queryFn: () => getAgencyIVR(agencyId!),
    enabled: Boolean(agencyId),
    refetchOnWindowFocus: false,
    select: (data: AxiosResponse<IVRResponseDto>) => data.data,
  });

export const useCreateAgencyIVRMutation = (
  agencyId: number,
  options?:
    | Omit<UseMutationOptions<AxiosResponse<IVRResponseDto, any>, unknown, CreateIVRDto, unknown>, 'mutationFn'>
    | undefined,
) => useMutation((data) => createAgencyIVR(agencyId, data), options);

export const useUpdateAgencyIVRMutation = (
  id: AgencyResponseDto['id'],
  options?:
    | Omit<UseMutationOptions<AxiosResponse<IVRResponseDto, any>, unknown, UpdateIvrDto, unknown>, 'mutationFn'>
    | undefined,
) => useMutation((data) => updateAgencyIVR(id, data), options);

interface UploadIVRAudioOptions {
  onSuccess?: (url: string) => void;
  onError?: (error: any) => void;
}

export const useUploadIVRAudio = ({ onSuccess, onError }: UploadIVRAudioOptions = {}) => {
  const { t } = useTranslation();
  const { addToast } = useSnackBar();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await client.post('/files/ivr', formData);
      return response.data;
    },
    onSuccess: (data) => {
      addToast({
        message: t('callsMenu.selectedStage.file.uploadSuccess'),
        color: 'success',
      });

      if (onSuccess && data && data.url) {
        onSuccess(data.url);
      }
    },
    onError: (error: any) => {
      addToast({
        message: t('callsMenu.selectedStage.file.uploadError'),
        color: 'error',
      });

      if (onError) {
        onError(error);
      }
    },
  });
};
